import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { useCallback } from 'react'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import useSessionStore from '@/stores/useSessionStore'

// Mirrors the backend enum: the opt-in submit IS the subscription. Double
// opt-in is tracked on the record separately, never as a state.
export type SmsSubscriptionState = 'SUBSCRIBED' | 'UNSUBSCRIBED'

export interface SmsSubscription {
  state: SmsSubscriptionState
}

// Standalone document, deliberately NOT part of the shared user fragment
// (useUserApi.ts) — if a brand's backend doesn't expose smsSubscription yet,
// only this query fails, never GetUser/UpdateUser. Brand scoping comes from
// the client's x-brand-id header; null means no record for this brand.
const query = gql`
  query GetSmsSubscription {
    user {
      smsSubscription {
        state
      }
    }
  }
`

export const useSmsSubscriptionApi = (
  enabled = true,
): UseQueryResult<SmsSubscription | null, Error> => {
  const client = useGraphQLClient()
  const userId = useSessionStore((state) => state.userId)

  const queryFn = useCallback(
    (): Promise<SmsSubscription | null> =>
      client
        .request<{ user: { smsSubscription: SmsSubscription | null } | null }>(query)
        .then(({ user }) => user?.smsSubscription ?? null),
    [client],
  )

  return useQuery<SmsSubscription | null, Error>({
    enabled: enabled && !!userId,
    queryKey: ['smsSubscription', userId],
    queryFn,
    retry: false,
  })
}
